import clsx from 'clsx'
import { useCallback, useMemo, useRef, useState, type CSSProperties, type PropsWithChildren, type ReactNode } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { useElementBoundingClientRectObserver } from 'hooks/useElementSize'

const Group = styled.div`
  min-width: 0;
  &.loading { min-height: var(--package-loading-height); }
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  border-block: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: 0;

  ${mediaQueryUp.tablet} {
    border-inline: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
    border-radius: ${props => props.theme.borderRadius.M};
  }

  ${mediaQueryUp.desktop} {
    border: 0;
    border-radius: 0;
    border-inline: 0;
  }
`

const Content = styled.div`
  display: grid;
  min-width: 0;

  ${mediaQueryUp.desktop} {
    grid-template-columns: minmax(${rem(220)}, 33.333%) minmax(0, 1fr);
  }
`

const Overview = styled.div`
  padding: ${rem(16)};
  min-width: 0;

  ${mediaQueryUp.desktop} {
    border-inline-end: 1px solid var(--palette-neutral-surface-light-foreground, ${props => props.theme.palette.neutral.surface.lightForeground});
  }
`

const Packages = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`

interface Props extends PropsWithChildren {
  overview: ReactNode
  banner?: ReactNode
  loading?: boolean
}

function AccommodationRoomPackageGroup({ overview, banner, children, loading = false }: Props) {
  const ref = useRef<HTMLDivElement>(null)
  const [settledSize, setSettledSize] = useState<{ width: number, height: number }>()
  const onResize = useCallback((rect: DOMRect) => {
    setSettledSize(previous => {
      if (loading) return previous?.width === rect.width ? previous : undefined
      return previous?.width === rect.width && previous.height === rect.height ? previous : { width: rect.width, height: rect.height }
    })
  }, [loading])
  useElementBoundingClientRectObserver(ref, onResize)
  const loadingStyle = useMemo<CSSProperties & { '--package-loading-height'?: string }>(() => ({
    '--package-loading-height': settledSize?.height ? `${settledSize.height}px` : undefined,
  }), [settledSize?.height])
  return <Group ref={ref} aria-busy={loading} className={clsx({ loading })} style={loadingStyle}>
    {banner}
    <Content>
      <Overview>{overview}</Overview>
      <Packages>{children}</Packages>
    </Content>
  </Group>
}

export default AccommodationRoomPackageGroup
